import { IconName } from '@const/icons.enum';
import { UserRole } from '@const/users.enum';
import { MenuItem } from '../interface/navigation.interface';

/**
 * Navigation Menu Configuration
 *
 * This configuration maps menu items to user roles based on the Azure AD role mapping:
 *
 * Role Mapping:
 * - Admin: Full access to all modules
 * - Tech Admin: User Management, Maintenance
 * - Operational Admin: Topology
 * - Operator: Operating
 * - Carrier: Carrier
 * - Dispatcher: Expedition
 * - Process POD: POD
 * - Broadcast Tendering Admin: BT Admin
 * - Broadcast Tendering Carrier: BT Carrier
 * - P2P Load Tendering Admin: LT Admin
 * - P2P Load Tendering Carrier: LT Carrier
 * - Material Admin: Material
 * - Observer Only: Observer (read-only access)
 */
export const NAVIGATION_CONFIG: MenuItem[] = [
  {
    name: 'nav.systemSettings',
    icon: IconName.NAV_FINE_TUNING,
    active: false,
    hasSubmenu: true,
    expanded: false,
    route: '/system-settings',
    allowedRoles: [UserRole.ADMIN, UserRole.OPERATIONAL_ADMIN, UserRole.TECH_ADMIN],
    submenu: [
      {
        name: 'nav.configuration',
        active: false,
        route: '/system-settings/configuration',
        allowedRoles: [UserRole.ADMIN, UserRole.OPERATIONAL_ADMIN],
      },
      {
        name: 'nav.messaging',
        active: false,
        route: '/system-settings/messaging',
        allowedRoles: [UserRole.ADMIN, UserRole.OPERATIONAL_ADMIN],
      },
      {
        name: 'nav.permission',
        active: false,
        route: '/system-settings/permission',
        allowedRoles: [UserRole.ADMIN, UserRole.TECH_ADMIN],
      },
    ],
  },
  {
    name: 'nav.operating',
    icon: IconName.NAV_PACKAGING,
    active: false,
    hasSubmenu: true,
    expanded: false,
    route: '/operating',
    // Operational Admin is admitted to the *parent* so the Loading Verification item below is
    // reachable; every other item in this submenu keeps its own narrower role set, so the role
    // sees Operating with exactly one entry under it.
    allowedRoles: [UserRole.ADMIN, UserRole.OPERATOR, UserRole.OPERATIONAL_ADMIN],
    submenu: [
      {
        name: 'nav.overview',
        active: false,
        route: '/operating/overview',
        allowedRoles: [UserRole.ADMIN, UserRole.OPERATOR],
      },
      {
        name: 'nav.loadingList',
        active: false,
        route: '/operating/loading-list',
        allowedRoles: [UserRole.ADMIN, UserRole.OPERATOR],
      },
      {
        name: 'nav.bookingOverview',
        active: false,
        route: '/operating/booking-overview',
        allowedRoles: [UserRole.ADMIN, UserRole.OPERATOR],
      },
      {
        // Bulk loading verification (CYLP-542) — points at the records page, which carries the
        // button that starts a scan. The scan flow itself is deliberately not in the menu: it
        // takes over the whole screen on a handheld and has no way back through the navigation.
        //
        // Shown only to the beta cohort, filtered in `left-navigation.ts` — the role set here
        // is wider than the pilot, which is why the role set alone cannot do it.
        name: 'nav.loadingVerification',
        active: false,
        route: '/operating/loading-verification',
        allowedRoles: [UserRole.ADMIN, UserRole.OPERATOR, UserRole.OPERATIONAL_ADMIN],
        beta: true,
      },
    ],
  },
  {
    name: 'nav.carrier',
    icon: IconName.NAV_TRUCK,
    active: false,
    hasSubmenu: true,
    expanded: false,
    route: '/carrier',
    allowedRoles: [UserRole.ADMIN, UserRole.CARRIER],
    submenu: [
      {
        name: 'nav.bookingManagement',
        active: false,
        route: '/carrier/booking-management',
        allowedRoles: [UserRole.ADMIN, UserRole.CARRIER],
      },
      {
        name: 'nav.statusTracking',
        active: false,
        route: '/carrier/status-tracking',
        allowedRoles: [UserRole.ADMIN, UserRole.CARRIER],
      },
      {
        name: 'nav.equipmentList',
        active: false,
        route: '/carrier/equipment-list',
        allowedRoles: [UserRole.ADMIN, UserRole.CARRIER],
      },
    ],
  },
  {
    name: 'nav.expedition',
    icon: IconName.NAV_BUILDING,
    active: false,
    hasSubmenu: true,
    expanded: false,
    route: '/expedition',
    allowedRoles: [UserRole.ADMIN, UserRole.DISPATCHER],
    submenu: [
      {
        name: 'nav.overview',
        active: false,
        route: '/expedition/overview',
        allowedRoles: [UserRole.ADMIN, UserRole.DISPATCHER],
      },
    ],
  },
  {
    name: 'nav.report',
    icon: IconName.NAV_PILLAR_CHART_INCREASE,
    active: false,
    hasSubmenu: true,
    expanded: false,
    route: '/report',
    allowedRoles: [
      UserRole.ADMIN,
      UserRole.MATERIAL_ADMIN,
      UserRole.OPERATOR,
      UserRole.OPERATIONAL_ADMIN,
    ],
    submenu: [
      {
        name: 'nav.downloads',
        active: false,
        route: '/report/downloads',
        allowedRoles: [UserRole.ADMIN, UserRole.MATERIAL_ADMIN],
      },
      {
        name: 'nav.materials',
        active: false,
        route: '/report/material',
        allowedRoles: [
          UserRole.ADMIN,
          UserRole.MATERIAL_ADMIN,
          UserRole.OPERATOR,
          UserRole.OPERATIONAL_ADMIN,
        ],
      },
      {
        name: 'nav.equipment',
        active: false,
        route: '/report/equipment',
        allowedRoles: [
          UserRole.ADMIN,
          UserRole.MATERIAL_ADMIN,
          UserRole.OPERATOR,
          UserRole.OPERATIONAL_ADMIN,
        ],
      },
      {
        name: 'nav.demurrage',
        active: false,
        route: '/report/demurrage',
        allowedRoles: [
          UserRole.ADMIN,
          UserRole.MATERIAL_ADMIN,
          UserRole.OPERATOR,
          UserRole.OPERATIONAL_ADMIN,
        ],
      },
      {
        name: 'nav.vehicleReport',
        active: false,
        route: '/report/vehicle-report',
        allowedRoles: [
          UserRole.ADMIN,
          UserRole.MATERIAL_ADMIN,
          UserRole.OPERATOR,
          UserRole.OPERATIONAL_ADMIN,
        ],
      },
      {
        name: 'nav.containerDefects',
        active: false,
        route: '/report/container-defects',
        allowedRoles: [
          UserRole.ADMIN,
          UserRole.MATERIAL_ADMIN,
          UserRole.OPERATOR,
          UserRole.OPERATIONAL_ADMIN,
        ],
      },
      {
        name: 'nav.containerInventory',
        active: false,
        route: '/report/container-inventory',
        allowedRoles: [
          UserRole.ADMIN,
          UserRole.MATERIAL_ADMIN,
          UserRole.OPERATOR,
          UserRole.OPERATIONAL_ADMIN,
        ],
      },
    ],
  },
  {
    name: 'nav.broadcastTendering',
    icon: IconName.NAV_LOUDSPEAKER,
    active: false,
    hasSubmenu: true,
    expanded: false,
    route: '/broadcast-tendering',
    allowedRoles: [
      UserRole.ADMIN,
      UserRole.BROADCAST_TENDERING_ADMIN,
      UserRole.BROADCAST_TENDERING_CARRIER,
    ],
    submenu: [
      {
        name: 'nav.admin',
        active: false,
        route: '/broadcast-tendering/admin',
        allowedRoles: [UserRole.ADMIN, UserRole.BROADCAST_TENDERING_ADMIN],
      },
      {
        name: 'nav.carrier',
        active: false,
        route: '/broadcast-tendering/carrier',
        allowedRoles: [UserRole.ADMIN, UserRole.BROADCAST_TENDERING_CARRIER],
      },
    ],
  },
  {
    name: 'nav.loadTendering',
    icon: IconName.NAV_TARGET,
    active: false,
    hasSubmenu: true,
    expanded: false,
    route: '/load-tendering',
    allowedRoles: [
      UserRole.ADMIN,
      UserRole.P2P_LOAD_TENDERING_ADMIN,
      UserRole.P2P_LOAD_TENDERING_CARRIER,
    ],
    submenu: [
      {
        name: 'nav.admin',
        active: false,
        route: '/load-tendering/admin',
        allowedRoles: [UserRole.ADMIN, UserRole.P2P_LOAD_TENDERING_ADMIN],
      },
      {
        name: 'nav.carrier',
        active: false,
        route: '/load-tendering/carrier',
        allowedRoles: [UserRole.ADMIN, UserRole.P2P_LOAD_TENDERING_CARRIER],
      },
    ],
  },
  {
    name: 'nav.proofOfDelivery',
    icon: IconName.NAV_REQUIREMENTS,
    active: false,
    hasSubmenu: true,
    expanded: false,
    route: '/pod',
    allowedRoles: [UserRole.ADMIN, UserRole.PROCESS_POD],
    submenu: [
      {
        name: 'nav.overview',
        active: false,
        route: '/pod/overview',
        allowedRoles: [UserRole.ADMIN, UserRole.PROCESS_POD],
      },
    ],
  },
  {
    name: 'nav.maintenance',
    icon: IconName.NAV_PROBLEM_SOLVING,
    active: false,
    hasSubmenu: true,
    expanded: false,
    route: '/maintenance',
    allowedRoles: [UserRole.ADMIN, UserRole.TECH_ADMIN],
    submenu: [
      {
        name: 'nav.systemAnnouncement',
        active: false,
        route: '/maintenance/system-announcment',
        allowedRoles: [UserRole.ADMIN, UserRole.TECH_ADMIN],
      },
      {
        name: 'nav.interfaceMessageViewer',
        active: false,
        route: '/maintenance/interface-message-viewer',
        allowedRoles: [UserRole.ADMIN, UserRole.TECH_ADMIN],
      },
      {
        name: 'nav.freightOrderViewer',
        active: false,
        route: '/maintenance/freight-order-viewer',
        allowedRoles: [UserRole.ADMIN, UserRole.TECH_ADMIN],
      },
      {
        name: 'nav.analytics',
        active: false,
        route: '/maintenance/analytics',
        allowedRoles: [UserRole.ADMIN, UserRole.TECH_ADMIN],
      },
    ],
  },
];
